import { useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { DashboardLayout } from "@/pages/Dashboard";
import { useLanguage, useAuth, API } from "@/App";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { UNITS, lookup } from "@/lib/grocery";
import { ListChecks, Plus, Trash2, Send, Loader2, ShoppingBasket } from "lucide-react";
import { toast } from "sonner";

const blankItem = { name: "", quantity: 1, unit: "piece", notes: "" };

export default function ShoppingListCreate() {
  const { t, language } = useLanguage();
  const { user, token } = useAuth();
  const navigate = useNavigate();
  const [items, setItems] = useState([{ ...blankItem }]);
  const [delivery, setDelivery] = useState({
    delivery_address: "", delivery_city: "", delivery_state: "", delivery_zip: "",
    delivery_phone: user?.phone || "", delivery_notes: ""
  });
  const [submitting, setSubmitting] = useState(false);

  const update = (idx, key, val) => {
    const copy = [...items]; copy[idx][key] = val; setItems(copy);
  };
  const addRow = () => setItems([...items, { ...blankItem }]);
  const removeRow = (idx) => setItems(items.filter((_, i) => i !== idx));

  const submit = async () => {
    const valid = items.filter((i) => i.name.trim());
    if (valid.length === 0) { toast.error(t("أضف منتجاً واحداً على الأقل", "Add at least one item")); return; }
    if (!delivery.delivery_address || !delivery.delivery_city || !delivery.delivery_state || !delivery.delivery_zip || !delivery.delivery_phone) {
      toast.error(t("يرجى تعبئة عنوان التوصيل", "Complete delivery info")); return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/orders`, {
        order_type: "shopping_list",
        items: valid.map((i) => ({ name: i.name, quantity: parseFloat(i.quantity) || 1, unit: i.unit, notes: i.notes })),
        ...delivery
      }, { headers: { Authorization: `Bearer ${token}` } });
      toast.success(t("تم استلام قائمتك! سيتواصل معك السائق قريباً.", "List received! Driver will contact you soon."));
      navigate("/grocery/orders");
    } catch (e) { toast.error(e.response?.data?.detail || t("فشل الإرسال", "Failed")); }
    finally { setSubmitting(false); }
  };

  const inputCls = "bg-slate-900 border border-slate-700 rounded-xl px-3 py-2.5 text-white text-sm focus:border-yellow-500 outline-none";

  return (
    <DashboardLayout title={t("قائمة تسوق ذكية", "Smart Shopping List")}>
      <div className="max-w-4xl space-y-6" data-testid="shopping-list-page">
        <div className="glass-card rounded-3xl p-6 lg:p-8 bg-luxury relative overflow-hidden">
          <div className="absolute -top-24 -right-24 w-80 h-80 bg-yellow-500/20 blur-[100px] rounded-full" />
          <div className="absolute -bottom-24 -left-24 w-80 h-80 bg-purple-500/20 blur-[100px] rounded-full" />
          <div className="relative">
            <Badge className="bg-gradient-to-r from-purple-500/20 to-yellow-500/20 text-yellow-300 border-yellow-500/30 mb-2">
              <ListChecks size={12} className="ml-1" /> {t("خدمة جديدة", "New service")}
            </Badge>
            <h2 className="text-2xl lg:text-3xl font-bold text-luxury">{t("اكتب قائمتك — نتسوّق ونوصّل لك", "Write your list — we'll shop & deliver")}</h2>
            <p className="text-slate-400 text-sm mt-1">{t("اكتب ما تحتاجه، وسيتولّى فريق سند العرب الشراء من أقرب متجر وتوصيله مع صورة الفاتورة.", "Write what you need; our team will shop from the nearest store and deliver with a receipt photo.")}</p>
          </div>
        </div>

        {/* Items */}
        <div className="glass-card rounded-2xl p-5 space-y-3">
          <h3 className="font-bold text-white flex items-center gap-2">
            <ShoppingBasket className="text-yellow-500" size={20} /> {t("قائمتك", "Your list")}
          </h3>
          {items.map((it, idx) => (
            <div key={idx} className="grid grid-cols-12 gap-2 items-center" data-testid={`list-item-${idx}`}>
              <input className={`${inputCls} col-span-5`} placeholder={t("مثلاً: 2 كجم لحم بقري حلال", "e.g. 2 kg halal beef")} value={it.name} onChange={(e) => update(idx, "name", e.target.value)} data-testid={`item-name-${idx}`} />
              <input className={`${inputCls} col-span-2`} type="number" min="0" step="0.1" placeholder={t("الكمية", "Qty")} value={it.quantity} onChange={(e) => update(idx, "quantity", e.target.value)} data-testid={`item-qty-${idx}`} />
              <select className={`${inputCls} col-span-2`} value={it.unit} onChange={(e) => update(idx, "unit", e.target.value)} data-testid={`item-unit-${idx}`}>
                {UNITS.map((u) => <option key={u.v} value={u.v}>{language === "ar" ? u.ar : u.en}</option>)}
              </select>
              <input className={`${inputCls} col-span-2`} placeholder={t("ملاحظة", "Note")} value={it.notes} onChange={(e) => update(idx, "notes", e.target.value)} />
              <button onClick={() => removeRow(idx)} className="col-span-1 text-red-400 hover:text-red-300" data-testid={`remove-item-${idx}`}><Trash2 size={16} className="mx-auto" /></button>
            </div>
          ))}
          <Button onClick={addRow} variant="outline" className="border-slate-700 text-white" data-testid="add-list-item-btn">
            <Plus size={14} className="ml-2" /> {t("إضافة منتج", "Add item")}
          </Button>
        </div>

        {/* Delivery */}
        <div className="glass-card rounded-2xl p-5 space-y-3">
          <h3 className="font-bold text-white">{t("معلومات التوصيل", "Delivery info")}</h3>
          <div className="grid md:grid-cols-2 gap-3">
            <input className={inputCls} placeholder={t("العنوان", "Address")} value={delivery.delivery_address} onChange={(e) => setDelivery({...delivery, delivery_address: e.target.value})} data-testid="sl-address" />
            <input className={inputCls} placeholder={t("المدينة", "City")} value={delivery.delivery_city} onChange={(e) => setDelivery({...delivery, delivery_city: e.target.value})} data-testid="sl-city" />
            <input className={inputCls} placeholder={t("الولاية (NY)", "State (NY)")} value={delivery.delivery_state} onChange={(e) => setDelivery({...delivery, delivery_state: e.target.value})} data-testid="sl-state" />
            <input className={inputCls} placeholder={t("ZIP", "ZIP")} value={delivery.delivery_zip} onChange={(e) => setDelivery({...delivery, delivery_zip: e.target.value})} data-testid="sl-zip" />
            <input className={inputCls} placeholder={t("الهاتف", "Phone")} value={delivery.delivery_phone} onChange={(e) => setDelivery({...delivery, delivery_phone: e.target.value})} data-testid="sl-phone" />
            <input className={inputCls} placeholder={t("ملاحظات", "Notes")} value={delivery.delivery_notes} onChange={(e) => setDelivery({...delivery, delivery_notes: e.target.value})} />
          </div>
        </div>

        <div className="flex justify-end">
          <Button onClick={submit} disabled={submitting} className="btn-primary" data-testid="submit-shopping-list-btn">
            {submitting ? <Loader2 className="animate-spin" size={16} /> : <><Send size={14} className="ml-2" /> {t("أرسل القائمة", "Send list")}</>}
          </Button>
        </div>
      </div>
    </DashboardLayout>
  );
}
